import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Button } from '../ui/button';
import { Calculator, TrendingUp, Info, Download, Share2, BookOpen } from 'lucide-react';
import { Badge } from '../ui/badge';
import jsPDF from 'jspdf';

const MicroEntrepriseSimulator = () => {
  const [inputs, setInputs] = useState({
    caAnnuel: '50000',
    activite: 'services-bic',
    versementLiberatoire: false,
    acre: false
  });

  const [results, setResults] = useState({});

  const TAUX = {
    'services-bic': { normal: 21.2, acre: 10.6, vl: 1.7, cfp: 0.3, label: 'Prestations de services BIC' },
    'services-bnc': { normal: 25.6, acre: 12.8, vl: 2.2, cfp: 0.2, label: 'Prestations de services BNC' },
    'liberal-cipav': { normal: 23.2, acre: 11.6, vl: 2.2, cfp: 0.2, label: 'Profession libérale (CIPAV)' },
    'achat-revente': { normal: 12.3, acre: 6.2, vl: 1.0, cfp: 0.1, label: 'Achat-revente de marchandises' }
  };

  const calculate = () => {
    const ca = parseFloat(inputs.caAnnuel) || 0;
    const taux = TAUX[inputs.activite];
    const tauxCotisation = inputs.acre ? taux.acre : taux.normal;
    const cotisations = ca * (tauxCotisation / 100);
    const cfp = ca * (taux.cfp / 100);

    let impot = 0;
    if (inputs.versementLiberatoire) {
      impot = ca * (taux.vl / 100);
    } else {
      const abattement = inputs.activite === 'achat-revente' ? 0.71 : inputs.activite === 'services-bic' ? 0.50 : 0.34;
      const revenuImposable = ca * (1 - abattement);
      if (revenuImposable > 11600) {
        impot = Math.min(revenuImposable - 11600, 29579 - 11600) * 0.11;
        if (revenuImposable > 29579) {
          impot += (revenuImposable - 29579) * 0.30;
        }
      }
    }

    const totalCharges = cotisations + cfp + impot;
    const revenuNet = ca - totalCharges;
    const tauxPrelevement = ca > 0 ? (totalCharges / ca) * 100 : 0;

    setResults({
      ca,
      cotisations: Math.round(cotisations),
      cfp: Math.round(cfp),
      impot: Math.round(impot),
      totalCharges: Math.round(totalCharges),
      revenuNet: Math.round(revenuNet),
      revenuNetMensuel: Math.round(revenuNet / 12),
      tauxPrelevement: tauxPrelevement.toFixed(1),
      tauxCotisation
    });
  };

  useEffect(() => { calculate(); }, [inputs]);

  const handleInputChange = (field, value) => {
    setInputs(prev => ({ ...prev, [field]: value }));
  };

  const shareLink = () => {
    const url = window.location.href;
    if (navigator.share) {
      navigator.share({ title: 'Simulateur Micro-Entreprise - Bozzam', url });
    } else {
      navigator.clipboard.writeText(url);
      alert('Lien copié !');
    }
  };

  const exportPDF = () => {
    const doc = new jsPDF();
    doc.setFillColor(30, 64, 175);
    doc.rect(0, 0, 210, 40, 'F');
    doc.setTextColor(255); doc.setFontSize(20);
    doc.text('Bozzam - Charges Micro-Entreprise', 15, 25);
    doc.setFontSize(10);
    doc.text('Simulation réalisée sur blog.bozzam.com', 15, 35);

    doc.setTextColor(0); let y = 55;
    doc.setFontSize(14); doc.text('Paramètres', 15, y); y += 10;
    doc.setFontSize(11);
    doc.text(`Activité : ${TAUX[inputs.activite].label}`, 15, y); y += 7;
    doc.text(`CA annuel : ${parseInt(inputs.caAnnuel).toLocaleString()} EUR`, 15, y); y += 7;
    doc.text(`ACRE : ${inputs.acre ? 'Oui' : 'Non'}`, 15, y); y += 7;
    doc.text(`Versement libératoire : ${inputs.versementLiberatoire ? 'Oui' : 'Non'}`, 15, y); y += 15;

    doc.setFontSize(14); doc.text('Résultats', 15, y); y += 10;
    doc.setFontSize(11);
    [
      [`Cotisations sociales (${results.tauxCotisation}%)`, `${results.cotisations?.toLocaleString()} EUR`],
      [`CFP`, `${results.cfp?.toLocaleString()} EUR`],
      [`Impôt sur le revenu`, `${results.impot?.toLocaleString()} EUR`],
      [`Total charges`, `${results.totalCharges?.toLocaleString()} EUR`],
      [`Revenu net annuel`, `${results.revenuNet?.toLocaleString()} EUR`],
      [`Revenu net mensuel`, `${results.revenuNetMensuel?.toLocaleString()} EUR`],
      [`Taux prélèvement global`, `${results.tauxPrelevement}%`],
    ].forEach(([l, v]) => { doc.text(`${l} : ${v}`, 15, y); y += 7; });

    y += 10;
    doc.setFontSize(9); doc.setTextColor(100);
    doc.text('Sources : URSSAF 2026, autoentrepreneur.urssaf.fr, service-public.fr', 15, y);
    doc.text('Taux BIC 21.2% | BNC 25.6% | CIPAV 23.2% | Achat-revente 12.3%', 15, y + 5);
    doc.save('simulation-micro-entreprise-bozzam.pdf');
  };

  return (
    <div className="max-w-6xl mx-auto p-6">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <Card className="h-fit">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calculator className="h-5 w-5 text-blue-600" />
              Paramètres micro-entreprise
            </CardTitle>
            <CardDescription>Calculez vos charges sociales et fiscales 2026</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div>
              <Label>Chiffre d'affaires annuel (€)</Label>
              <Input
                data-testid="ca-input"
                type="text" inputMode="numeric"
                value={inputs.caAnnuel}
                onChange={(e) => handleInputChange('caAnnuel', e.target.value.replace(/[^0-9]/g, ''))}
                placeholder="50000" className="mt-1"
              />
            </div>

            <div>
              <Label>Type d'activité</Label>
              <div className="grid grid-cols-1 gap-2 mt-2">
                {Object.entries(TAUX).map(([key, val]) => (
                  <Button
                    key={key}
                    data-testid={`activite-${key}-btn`}
                    variant={inputs.activite === key ? 'default' : 'outline'}
                    className={`text-left justify-start text-sm ${inputs.activite === key ? 'bg-blue-600 hover:bg-blue-700' : ''}`}
                    onClick={() => handleInputChange('activite', key)}
                  >
                    {val.label} ({val.normal}%)
                  </Button>
                ))}
              </div>
            </div>

            <div className="flex items-center gap-4">
              <label className="flex items-center gap-2 cursor-pointer" data-testid="acre-toggle">
                <input
                  type="checkbox" checked={inputs.acre}
                  onChange={(e) => handleInputChange('acre', e.target.checked)}
                  className="w-4 h-4 rounded border-gray-300 text-blue-600"
                />
                <span className="text-sm font-medium">ACRE (1ère année)</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer" data-testid="vl-toggle">
                <input
                  type="checkbox" checked={inputs.versementLiberatoire}
                  onChange={(e) => handleInputChange('versementLiberatoire', e.target.checked)}
                  className="w-4 h-4 rounded border-gray-300 text-blue-600"
                />
                <span className="text-sm font-medium">Versement libératoire IR</span>
              </label>
            </div>

            <div className="p-4 bg-blue-50 rounded-lg">
              <div className="flex items-start gap-2">
                <BookOpen className="h-4 w-4 text-blue-600 mt-0.5" />
                <div className="text-sm text-blue-800">
                  <p className="font-medium mb-1">Sources légales 2026</p>
                  <ul className="list-disc list-inside text-xs space-y-1">
                    <li>URSSAF - autoentrepreneur.urssaf.fr - Taux 2026</li>
                    <li>Décret n°2025-943 plafonnant BNC à 25.6%</li>
                    <li>Service-public.fr - Barème IR 2026 (revenus 2025)</li>
                    <li>Seuil micro 2026 : 188 700€ (commerce) / 77 700€ (services)</li>
                  </ul>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-green-600" />
              Résultats
            </CardTitle>
            <div className="flex gap-2 flex-wrap">
              <Badge className="bg-blue-100 text-blue-800">{TAUX[inputs.activite].label}</Badge>
              {inputs.acre && <Badge className="bg-green-100 text-green-800">ACRE</Badge>}
              {inputs.versementLiberatoire && <Badge className="bg-purple-100 text-purple-800">VFL</Badge>}
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="p-4 bg-blue-50 rounded-lg">
              <p className="text-sm font-medium text-blue-600 mb-1">Chiffre d'affaires annuel</p>
              <p className="text-2xl font-bold text-blue-900" data-testid="result-ca">{results.ca?.toLocaleString()}€</p>
            </div>

            <div className="space-y-2">
              <div className="flex justify-between items-center p-3 bg-red-50 rounded">
                <span className="text-sm font-medium text-red-700">Cotisations sociales ({results.tauxCotisation}%)</span>
                <span className="text-sm font-bold text-red-900" data-testid="result-cotisations">{results.cotisations?.toLocaleString()}€</span>
              </div>
              <div className="flex justify-between items-center p-3 bg-red-50 rounded">
                <span className="text-sm font-medium text-red-700">CFP (formation)</span>
                <span className="text-sm font-bold text-red-900">{results.cfp?.toLocaleString()}€</span>
              </div>
              <div className="flex justify-between items-center p-3 bg-red-50 rounded">
                <span className="text-sm font-medium text-red-700">Impôt sur le revenu</span>
                <span className="text-sm font-bold text-red-900">{results.impot?.toLocaleString()}€</span>
              </div>
              <div className="flex justify-between items-center p-3 bg-red-100 rounded border-2 border-red-200">
                <span className="text-sm font-bold text-red-800">Total prélèvements</span>
                <span className="text-sm font-bold text-red-900" data-testid="result-total">{results.totalCharges?.toLocaleString()}€</span>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="p-4 bg-green-50 rounded-lg border-2 border-green-200">
                <p className="text-sm font-medium text-green-600 mb-1">Revenu net annuel</p>
                <p className="text-2xl font-bold text-green-900" data-testid="result-net">{results.revenuNet?.toLocaleString()}€</p>
              </div>
              <div className="p-4 bg-green-50 rounded-lg border-2 border-green-200">
                <p className="text-sm font-medium text-green-600 mb-1">Revenu net mensuel</p>
                <p className="text-2xl font-bold text-green-900">{results.revenuNetMensuel?.toLocaleString()}€</p>
              </div>
            </div>

            <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
              <p className="text-sm text-amber-800">
                <span className="font-semibold">Taux de prélèvement global : {results.tauxPrelevement}%</span> — Sur chaque euro facturé, vous conservez {(100 - parseFloat(results.tauxPrelevement || 0)).toFixed(1)} centimes.
              </p>
            </div>

            <div className="grid grid-cols-3 gap-3">
              <Button onClick={shareLink} variant="outline" data-testid="share-btn"><Share2 className="h-4 w-4 mr-2" /> Partager</Button>
              <Button onClick={exportPDF} variant="outline" data-testid="export-btn"><Download className="h-4 w-4 mr-2" /> PDF</Button>
              <Button className="bg-blue-600 hover:bg-blue-700" onClick={() => window.open(process.env.REACT_APP_BOZZAM_APP_URL, '_blank')}>Bozzam App</Button>
            </div>

            <div className="text-xs text-gray-500 text-center pt-2 border-t">
              <p>Sources : URSSAF 2026 - autoentrepreneur.urssaf.fr - service-public.fr</p>
              <p>Taux BIC 21.2% | BNC 25.6% | CIPAV 23.2% | Achat-revente 12.3%</p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default MicroEntrepriseSimulator;
